import { getParsedAccessToken } from '../components/callback/callBack.service';
import { claimValue, parseJwt } from '../util/jwt';

/**
 * The entity the stored token is scoped to, or null.
 *
 * This is the authority for "which account am I in" (DV-3012 / D3) — the
 * backend scopes every request on this claim, so it outranks
 * `localStorage.selectedEntity`, which the pre-DV-3012 guard compared against
 * and which can drift from the claim. `selectedOrg` still answers a different
 * question — whether an entity was CHOSEN in this tab.
 *
 * Never throws: it runs on the pre-render path, and `parseJwt` will throw on a
 * malformed token.
 */
export const entityClaim = (): string | null =>
  entityClaimOf(getParsedAccessToken()?.access_token);

/**
 * The entity a given access token is scoped to, or null. Unlike `entityClaim()`
 * this does not touch localStorage — `getParsedAccessToken()` deletes a stored
 * token it cannot parse — so it is safe on the per-request path, which already
 * holds the token it attached. Never throws.
 */
export const entityClaimOf = (token: string | null | undefined): string | null => {
  if (!token) return null;

  try {
    return claimValue(parseJwt(token).EntityUUID);
  } catch {
    return null;
  }
};

/**
 * An `EntityUUID` claim as a single id, or null. Shared with the login path,
 * which reads the claim off the freshly exchanged token before it is stored.
 */
export { claimValue as normalizeEntityClaim } from '../util/jwt';

/**
 * The user (`sub`) of a given / the stored access token. Re-exported from the
 * one implementation in `util/jwt` — see `subjectOfStoredToken` for why it
 * lives there, and why the stored read does not delete a malformed token.
 */
export { subjectOf, subjectOfStoredToken as tokenSubject } from '../util/jwt';

/**
 * The entity the stored token is ANCHORED to (DV-3421) — the same derivation
 * the backend guard uses to build the allowed set. See `scopeRootOfStoredToken`.
 */
export { scopeRootOf, scopeRootOfStoredToken as scopeRootOfToken } from '../util/jwt';

/**
 * The stored token's claims, decoded once, for a caller that needs several of
 * them — `reconcileScope` reads the user, the root and the issue time on every
 * trigger (review of 0a17c730, item 9). See `storedClaims`.
 */
export { storedClaims as tokenClaims } from '../util/jwt';

